// Tasks and routines on Vandaag: in their block, "Vandaag ergens", "Nog open" (N1), "Nog deze week" (N2),
// "Opnieuw beginnen" (N4), "Welkom terug" (N3).
import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { stillThisWeek, weekComplete, weekCount, weekStart, weekTarget } from "@shared/routines";
import { pausedDays } from "@shared/rhythm";
import { addDays, displayDate } from "@shared/time";
import { isPausedIn, useRhythm } from "../lib/rhythm";
import { Sheet } from "../components/Sheet";
import { openTask } from "../components/TaskSheet";
import { Button, cn } from "../components/ui";
import { db, getMeta, setMeta, type DayBlock, type PlanEntry } from "../lib/db";
import { setBlockStatus } from "../lib/days";
import { useCollapsed } from "../lib/hooks";
import {
  completeRoutine,
  plannedThrough,
  setDismissedWeek,
  setRoutineEntryDone,
  uncompleteRoutine,
  useCompletions,
  usePlannedThrough,
  useRoutines,
  useWeekRoutineEntries,
} from "../lib/routines";
import { planTask, restoreEntries, setEntryDone, setTaskStatus, unplanEntries } from "../lib/tasks";

/** "Nog open" shows at most this many; the rest behind "en x meer" (N1 AC3). */
const NOG_OPEN_VISIBLE = 5;

const shortDate = new Intl.DateTimeFormat("nl-NL", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
const dayName = (iso: string) => shortDate.format(new Date(`${iso}T00:00:00Z`));

/** Planned and done task entries of one day, by block id ("" = no block or a block not on this day). */
export function useDayEntries(date: string, blockIds: Set<string>) {
  const entries = useLiveQuery(
    () =>
      db.plan_entries
        .where("plan_date")
        .equals(date)
        .filter((e) => !e.deleted_at && (e.status === "planned" || e.status === "done"))
        .toArray(),
    [date],
  );
  const byBlock = new Map<string, PlanEntry[]>();
  for (const e of entries ?? []) {
    const key = e.day_block_id && blockIds.has(e.day_block_id) ? e.day_block_id : "";
    byBlock.set(key, [...(byBlock.get(key) ?? []), e]);
  }
  for (const list of byBlock.values()) list.sort((a, b) => a.position - b.position || (a.created_at < b.created_at ? -1 : 1));
  return byBlock;
}

function toggleEntry(entry: PlanEntry) {
  const done = entry.status !== "done";
  const set = entry.routine_id ? setRoutineEntryDone : setEntryDone;
  void set(entry, done);
  if (done) {
    toast(`${entry.title} afgevinkt`, {
      duration: 5000,
      action: { label: "Ongedaan maken", onClick: () => void set({ ...entry, status: "done" }, false) },
    });
  }
}

/** Next to a routine on its day: the week counter ("2 van 3", V4 AC3), or "Al gedaan deze week" (RO2 AC5). */
function RoutineNote({ entry }: { entry: PlanEntry }) {
  const routines = useRoutines();
  const completions = useCompletions();
  const rhythm = useRhythm();
  const routine = routines?.find((r) => r.id === entry.routine_id);
  if (!routine || !completions) return null;
  if (routine.weekly_target) {
    return (
      <span className="shrink-0 text-sm text-ink-soft tabular">{`${weekCount(routine.id, completions, entry.plan_date)} van ${weekTarget(routine, entry.plan_date, isPausedIn(rhythm))}`}</span>
    );
  }
  if (entry.status === "planned" && weekComplete(routine, completions, entry.plan_date)) {
    return <span className="shrink-0 text-sm text-ink-soft">Al gedaan deze week</span>;
  }
  return null;
}

/** A sport block with only a routine in it (the gym): shown as that routine, one line (B32). */
export function soleRoutine(block: { slot_kind: string; status?: string }, entries: PlanEntry[]): boolean {
  return block.slot_kind === "sport" && !!block.status && entries.length === 1 && !!entries[0]!.routine_id;
}

/** The gym as the sport block itself: one check for both, with the week counter. */
export function SoleRoutine({
  block,
  entry,
  variant,
  checkable,
}: {
  block: { id: string };
  entry: PlanEntry;
  variant: "now" | "full" | "compact";
  checkable: boolean;
}) {
  const done = entry.status === "done";
  const size = variant === "now" ? "text-xl font-bold" : variant === "full" ? "text-lg font-semibold" : "text-base";
  const label = (
    <>
      <span className={cn("min-w-0 flex-1 [overflow-wrap:anywhere]", size, done && "text-done line-through decoration-2")}>
        {entry.title}
      </span>
      <RoutineNote entry={entry} />
    </>
  );
  if (!checkable) return <div className="flex min-h-12 items-center gap-3 py-1">{label}</div>;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      onClick={() => {
        toggleEntry(entry);
        // The block goes along, so "x van y klaar" and the notifications know it too.
        void setBlockStatus(block as unknown as DayBlock, done ? "open" : "done");
      }}
      className={cn("flex min-h-12 w-full items-center gap-3 rounded-2xl px-2 py-1 text-left", variant === "now" && "bg-surface px-3 py-3")}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-full border-2",
          done ? "border-pine bg-pine text-paper" : "border-ink-soft",
        )}
      >
        {done && <Check className="size-4" strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
}

/** The tasks in a block, to fold in and out (B32); folded, "2 van 3" stays in sight. */
export function FoldedEntries({ id, entries, large = false }: { id: string; entries: PlanEntry[]; large?: boolean }) {
  const [folded, toggle] = useCollapsed(`block:${id}`);
  if (!entries.length) return null;
  const done = entries.filter((e) => e.status === "done").length;
  return (
    <div className="mt-1 ml-3">
      <button
        type="button"
        aria-expanded={!folded}
        onClick={toggle}
        className="flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm text-ink-soft"
      >
        <span className="tabular">{`${done} van ${entries.length}`}</span>
        <ChevronDown aria-hidden className={cn("size-4 transition-transform", !folded && "rotate-180")} />
        <span className="sr-only">{folded ? "Tonen" : "Verbergen"}</span>
      </button>
      {!folded && <EntryList entries={entries} large={large} />}
    </div>
  );
}

/** Tasks inside a block: check off, or open the task. */
export function EntryList({ entries, large = false }: { entries: PlanEntry[]; large?: boolean }) {
  if (!entries.length) return null;
  return (
    <ul aria-label="In dit blok" className="mt-1 ml-3 flex flex-col gap-1 border-l-2 border-mist pl-3">
      {entries.map((e) => {
        const done = e.status === "done";
        return (
          <li key={e.id} className="flex items-center gap-1">
            <button
              type="button"
              role="checkbox"
              aria-checked={done}
              onClick={() => toggleEntry(e)}
              className={cn(
                "flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-xl bg-paper px-2 text-left text-[0.95rem]",
                large && "py-2",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-md border-2",
                  done ? "border-pine bg-pine text-paper" : "border-ink-soft",
                )}
              >
                {done && <Check className="size-4" strokeWidth={3} />}
              </span>
              <span className={cn("min-w-0 flex-1 [overflow-wrap:anywhere]", done && "text-done line-through decoration-2")}>
                {e.title}
              </span>
              {e.routine_id && <RoutineNote entry={e} />}
            </button>
            {e.task_id && (
              <button
                type="button"
                onClick={() => openTask(e.task_id)}
                aria-label={`${e.title} openen`}
                className="flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-soft"
              >
                <ChevronRight aria-hidden className="size-5" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Tasks for today without a block. */
export function Elsewhere({ entries }: { entries: PlanEntry[] }) {
  if (!entries.length) return null;
  return (
    <section aria-labelledby="ergens" className="mb-5 rounded-2xl border border-mist bg-surface p-3">
      <h2 id="ergens" className="font-semibold">
        Vandaag ergens
      </h2>
      <EntryList entries={entries} />
    </section>
  );
}

function useOpenBefore(date: string) {
  return useLiveQuery(
    () =>
      db.plan_entries
        .where("status")
        .equals("planned")
        .filter((e) => !e.deleted_at && e.plan_date < date && e.kind === "task")
        .sortBy("plan_date"),
    [date],
  );
}

/** Routines that are in "Nog deze week" now, read once (for Opnieuw beginnen). */
async function stillThisWeekIds(date: string): Promise<string[]> {
  const from = weekStart(date);
  const [routines, completions, entries] = await Promise.all([
    db.routines.toArray(),
    db.routine_completions.toArray(),
    db.plan_entries
      .where("plan_date")
      .between(from, addDays(from, 6), true, true)
      .filter((e) => e.kind === "routine")
      .toArray(),
  ]);
  const [templates, rules, overrides] = await Promise.all([
    db.day_templates.toArray(),
    db.day_template_rules.toArray(),
    db.day_overrides.toArray(),
  ]);
  const isPaused = pausedDays(templates, rules, overrides);
  return stillThisWeek(routines, entries, completions, date, await plannedThrough(from), isPaused).map((i) => i.routine.id);
}

/**
 * Opnieuw beginnen (N4): every task planned before today back to the list, and "Nog deze week" empty
 * for the rest of this week. Ten seconds to undo.
 */
export async function startOver(entries: PlanEntry[], date: string) {
  const tasks = entries.filter((e) => e.kind === "task");
  const routineIds = await stillThisWeekIds(date);
  if (!tasks.length && !routineIds.length) {
    toast("Er staat niets meer open van eerdere dagen.");
    return;
  }
  const ids = tasks.map((e) => e.id);
  await unplanEntries(tasks, "start_over");
  await setDismissedWeek(routineIds, weekStart(date));
  const parts = [
    ...(tasks.length ? [tasks.length === 1 ? "1 taak terug naar Te doen" : `${tasks.length} taken terug naar Te doen`] : []),
    ...(routineIds.length ? ["Nog deze week is leeg"] : []),
  ];
  toast(parts.join(". "), {
    duration: 10_000,
    action: {
      label: "Ongedaan maken",
      onClick: async () => {
        await restoreEntries(ids);
        await setDismissedWeek(routineIds, null);
      },
    },
  });
}

/** Nog deze week (N2): weekly routines that were missed or found no place, until Sunday, without a time. */
export function NogDezeWeek({ date }: { date: string }) {
  const routines = useRoutines();
  const completions = useCompletions();
  const entries = useWeekRoutineEntries(date);
  const through = usePlannedThrough(date);
  const rhythm = useRhythm();
  if (!routines || !completions || !entries || through === undefined || !rhythm) return null;
  const list = stillThisWeek(routines, entries, completions, date, through, isPausedIn(rhythm));
  if (!list.length) return null;

  async function check({ routine, missedOn }: (typeof list)[number]) {
    // A missed fixed day: the check counts for that time, so the right one leaves the list.
    const id = await completeRoutine(routine, missedOn ?? date);
    if (!id) return;
    toast(`${routine.title} afgevinkt`, { duration: 5000, action: { label: "Ongedaan maken", onClick: () => void uncompleteRoutine(id) } });
  }

  return (
    <section aria-labelledby="nog-deze-week" className="mb-5 rounded-2xl border border-mist bg-surface p-3">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="nog-deze-week" className="font-semibold">
          Nog deze week
        </h2>
        <span className="text-sm text-ink-soft">Wanneer het uitkomt, tot en met zondag.</span>
      </div>
      <ul className="flex flex-col gap-1">
        {list.map((item) => (
          <li key={item.routine.id}>
            <button
              type="button"
              onClick={() => void check(item)}
              aria-label={`${item.routine.title} afvinken`}
              className="flex min-h-12 w-full items-center gap-3 rounded-xl bg-paper px-2 text-left"
            >
              <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-md border-2 border-ink-soft" />
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{item.routine.title}</span>
              {item.remaining > 1 && <span className="shrink-0 text-sm text-ink-soft">nog {item.remaining} keer</span>}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Nog open (N1): tasks of earlier days that are not done. No red, no "te laat"; it blocks nothing. */
export function NogOpen({ date }: { date: string }) {
  const open = useOpenBefore(date);
  const [all, setAll] = useState(false);
  if (!open?.length) return null;
  const visible = all ? open : open.slice(0, NOG_OPEN_VISIBLE);
  const tomorrow = addDays(date, 1);

  async function act(entry: PlanEntry, action: "today" | "tomorrow" | "list" | "away") {
    const taskId = entry.task_id;
    if (!taskId) return;
    // Vandaag: choose a block (N1 AC2).
    if (action === "today") return openTask(taskId, { plan: true, reason: "nog_open_today" });
    if (action === "tomorrow") {
      await planTask(taskId, { date: tomorrow }, "nog_open_tomorrow");
      toast("Op morgen gezet");
    }
    if (action === "list") {
      await unplanEntries([entry], "back_to_list");
      toast("Zonder dag, bij Te doen", { action: { label: "Ongedaan maken", onClick: () => void restoreEntries([entry.id]) } });
    }
    if (action === "away") {
      await setTaskStatus(taskId, "cancelled");
      toast("Weggezet", {
        duration: 10_000,
        action: {
          label: "Ongedaan maken",
          onClick: async () => {
            await setTaskStatus(taskId, "open");
            await restoreEntries([entry.id]);
          },
        },
      });
    }
  }

  return (
    <section aria-labelledby="nog-open" className="mb-6 rounded-2xl border-2 border-mist bg-surface p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="nog-open" className="text-lg font-bold">
          Nog open
        </h2>
        <span className="text-sm text-ink-soft">Van eerdere dagen. Jij kiest wat ermee gebeurt.</span>
      </div>
      <ul className="flex flex-col gap-3">
        {visible.map((e) => (
          <li key={e.id} className="rounded-xl bg-paper p-2">
            <button type="button" onClick={() => e.task_id && openTask(e.task_id)} className="min-h-11 w-full text-left">
              <span className="block [overflow-wrap:anywhere]">{e.title}</span>
              <span className="text-sm text-ink-soft">Stond op {dayName(e.plan_date)}</span>
            </button>
            <div className="mt-1 grid grid-cols-4 gap-1">
              {(
                [
                  ["today", "Vandaag"],
                  ["tomorrow", "Morgen"],
                  ["list", "Zonder dag"],
                  ["away", "Weg"],
                ] as const
              ).map(([action, label]) => (
                <button
                  key={action}
                  type="button"
                  onClick={() => void act(e, action)}
                  aria-label={`${e.title}: ${label}`}
                  className="min-h-11 rounded-lg border-2 border-mist px-1 text-sm font-semibold"
                >
                  {label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      {open.length > NOG_OPEN_VISIBLE && !all && (
        <button type="button" onClick={() => setAll(true)} className="mt-2 min-h-11 px-1 font-semibold text-pine">
          en {open.length - NOG_OPEN_VISIBLE} meer
        </button>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="secondary" className="flex-1" onClick={() => void unplanAll(open)}>
          Alles terug naar Te doen
        </Button>
        <Button variant="quiet" onClick={() => void startOver(open, date)}>
          <RotateCcw aria-hidden className="size-4" />
          Opnieuw beginnen
        </Button>
      </div>
    </section>
  );
}

async function unplanAll(entries: PlanEntry[]) {
  const ids = entries.map((e) => e.id);
  await unplanEntries(entries, "all_to_list");
  toast("Alles staat weer bij Te doen", { duration: 10_000, action: { label: "Ongedaan maken", onClick: () => void restoreEntries(ids) } });
}

/** Days between two dates, exclusive (12 and 15 October: 2 days in between). */
function daysBetween(from: string, to: string) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) - 1;
}

/** Welkom terug (N3): after 2 or more days without opening the app, offer to clear those days at once. */
export function WelcomeBack({ date }: { date: string }) {
  const [state, setState] = useState<{
    missed: number;
    entries: PlanEntry[];
    deadlines: { id: string; title: string; due_on: string }[];
  } | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const last = await getMeta<string>("lastOpenedDate");
      await setMeta("lastOpenedDate", date);
      if (!last || last >= date) return;
      const missed = daysBetween(last, date);
      if (missed < 2) return;
      const entries = await db.plan_entries
        .where("status")
        .equals("planned")
        .filter((e) => !e.deleted_at && e.kind === "task" && e.plan_date > last && e.plan_date < date)
        .toArray();
      const soon = addDays(date, 7);
      const deadlines = (await db.tasks.toArray())
        .filter((t) => !t.deleted_at && t.due_on && t.due_on >= date && t.due_on <= soon && t.status !== "done" && t.status !== "cancelled")
        .map((t) => ({ id: t.id, title: t.title, due_on: t.due_on! }))
        .sort((a, b) => (a.due_on < b.due_on ? -1 : 1));
      if (alive && (entries.length || deadlines.length)) setState({ missed, entries, deadlines });
    })();
    return () => {
      alive = false;
    };
  }, [date]);

  if (!state) return null;
  const close = () => setState(null);
  return (
    <Sheet open onOpenChange={(o) => !o && close()} title="Welkom terug" description={`Je was er ${state.missed} dagen niet.`}>
      <div className="flex flex-col gap-4">
        {state.entries.length > 0 && (
          <>
            <p>
              {state.entries.length === 1 ? "Er staat 1 taak open" : `Er staan ${state.entries.length} taken open`} van die dagen. Terug
              naar Te doen? Dan begin je vandaag schoon.
            </p>
            <Button
              onClick={async () => {
                await unplanEntries(state.entries, "welcome_back");
                toast("Terug naar Te doen");
                close();
              }}
            >
              Ja, terug naar Te doen
            </Button>
          </>
        )}
        {state.deadlines.length > 0 && (
          <section aria-labelledby="deadlines">
            <h3 id="deadlines" className="mb-1 font-semibold">
              Deadlines deze week
            </h3>
            <ul>
              {state.deadlines.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => openTask(d.id)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">{d.title}</span>
                    <span className="text-sm text-ink-soft">{displayDate(d.due_on)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <Button variant="secondary" onClick={close}>
          {state.entries.length ? "Nee, zelf bekijken" : "Sluiten"}
        </Button>
      </div>
    </Sheet>
  );
}
